import { useFinanceApi } from 'infrastructure/api';
import { createEmptySalaryRanges, POSITION_PROFILE_OFFICES } from '../../constants';
import type { PositionProfileSalaryRangeDto, SalaryRange } from '../../types';

const FINANCE_RESOURCE_URL = '/position-profile-finance';

type FinanceRecord = {
  id: string;
  value: number;
};

const parseSalaryValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const parsedValue = Number(String(value).replace(/[\s,]/g, ''));
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const hasSalaryValue = (value: number | null, originalValue: number | null, currentId: string | null) =>
  value !== null || Boolean(currentId && originalValue === null);

const usePositionProfileFinance = () => {
  const { sendPostRequest } = useFinanceApi();

  const resolveFinanceRecords = async (ids: string[]): Promise<Map<string, number>> => {
    if (!ids.length) return new Map();

    const response = await sendPostRequest(`${FINANCE_RESOURCE_URL}/search`, { ids });
    const records = (await response.json()) as FinanceRecord[];

    return new Map(records.map(record => [record.id, Number(record.value)]));
  };

  const enrichSalaryRanges = async (ranges: PositionProfileSalaryRangeDto[]): Promise<SalaryRange[]> => {
    const ids = Array.from(
      new Set(
        ranges.flatMap(range => [range.minSalaryId, range.maxSalaryId]).filter((id): id is string => Boolean(id)),
      ),
    );
    let valueById = new Map<string, number>();

    try {
      valueById = await resolveFinanceRecords(ids);
    } catch {
      // A profile remains readable when salary data is unavailable to the current user.
    }

    return createEmptySalaryRanges().map(defaultRange => {
      const range = ranges.find(item => item.officeId === defaultRange.officeId);
      const minValue = range?.minSalaryId ? (valueById.get(range.minSalaryId) ?? null) : null;
      const maxValue = range?.maxSalaryId ? (valueById.get(range.maxSalaryId) ?? null) : null;

      return {
        ...defaultRange,
        minSalaryId: range?.minSalaryId ?? null,
        maxSalaryId: range?.maxSalaryId ?? null,
        minValue,
        maxValue,
        originalMinValue: minValue,
        originalMaxValue: maxValue,
      };
    });
  };

  const createFinanceRecord = async (value: number): Promise<string> => {
    const response = await sendPostRequest(FINANCE_RESOURCE_URL, { value });
    const record = (await response.json()) as FinanceRecord;
    return record.id;
  };

  const getSalaryId = async (
    value: number | null,
    originalValue: number | null,
    currentId: string | null,
  ): Promise<string | null> => {
    if (currentId && value === originalValue) return currentId;
    if (value === null) return null;
    return createFinanceRecord(value);
  };

  const buildSalaryRangePayload = async (ranges: SalaryRange[]): Promise<PositionProfileSalaryRangeDto[]> => {
    const normalizedRanges = ranges.map(range => ({
      ...range,
      minValue: parseSalaryValue(range.minValue),
      maxValue: parseSalaryValue(range.maxValue),
    }));
    const incompleteRange = normalizedRanges.find(range => {
      const hasMinValue = hasSalaryValue(range.minValue, range.originalMinValue, range.minSalaryId);
      const hasMaxValue = hasSalaryValue(range.maxValue, range.originalMaxValue, range.maxSalaryId);

      return hasMinValue !== hasMaxValue;
    });

    if (incompleteRange) {
      const office = POSITION_PROFILE_OFFICES.find(item => item.officeId === incompleteRange.officeId);
      return Promise.reject(
        `Both min and max salary values are required for ${office?.country ?? incompleteRange.officeId}.`,
      );
    }

    return Promise.all(
      normalizedRanges
        .filter(
          range =>
            hasSalaryValue(range.minValue, range.originalMinValue, range.minSalaryId) ||
            hasSalaryValue(range.maxValue, range.originalMaxValue, range.maxSalaryId),
        )
        .map(async range => ({
          officeId: range.officeId,
          minSalaryId: await getSalaryId(range.minValue, range.originalMinValue, range.minSalaryId),
          maxSalaryId: await getSalaryId(range.maxValue, range.originalMaxValue, range.maxSalaryId),
        })),
    );
  };

  return { enrichSalaryRanges, buildSalaryRangePayload };
};

export default usePositionProfileFinance;
